import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Download, FileText } from "lucide-react";

/**
 * Full-screen lightbox for previewing image + PDF attachments.
 * Renders as a portal-less fixed overlay; click backdrop or X to close.
 *
 * Props:
 *   attachment: { id, name, type ('image'|'pdf'|'text'|other), data (data-url) } | null
 *   onClose: () => void
 */
export const MediaLightbox = ({ attachment, onClose }) => {
    // Close on Escape (and prevent the parent Dialog from also closing).
    useEffect(() => {
        if (!attachment) return;
        const onKey = (e) => {
            if (e.key === "Escape") {
                e.stopPropagation();
                e.preventDefault();
                onClose?.();
            }
        };
        // capture=true so we intercept BEFORE radix Dialog's own listener
        window.addEventListener("keydown", onKey, true);
        // Lock body scroll while open
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey, true);
            document.body.style.overflow = prev;
        };
    }, [attachment, onClose]);

    if (!attachment) return null;

    const handleBackdrop = (e) => {
        e.stopPropagation();
        onClose?.();
    };

    const isImage = attachment.type === "image";
    const isPdf =
        attachment.type === "pdf" ||
        (attachment.data || "").startsWith("data:application/pdf") ||
        (attachment.name || "").toLowerCase().endsWith(".pdf");

    return createPortal(
        <div
            onClick={handleBackdrop}
            className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 fade-in"
            data-testid="media-lightbox"
            role="dialog"
            aria-modal="true"
        >
            {/* Close + Download bar */}
            <div className="absolute top-4 right-4 flex items-center gap-2 z-[201]" onClick={(e) => e.stopPropagation()}>
                <a
                    href={attachment.data}
                    download={attachment.name || "attachment"}
                    className="p-2 rounded-full border border-meye-border bg-black/60 text-zinc-300 hover:text-meye-cyan hover:border-meye-cyan/60 transition-colors"
                    title="Download"
                    data-testid="lightbox-download"
                >
                    <Download size={16} strokeWidth={2} />
                </a>
                <button
                    type="button"
                    onClick={onClose}
                    className="p-2 rounded-full border border-meye-border bg-black/60 text-zinc-300 hover:text-white hover:border-white transition-colors"
                    aria-label="Close preview"
                    data-testid="lightbox-close"
                >
                    <X size={16} strokeWidth={2} />
                </button>
            </div>

            {/* Filename label */}
            <div className="absolute top-4 left-4 z-[201] max-w-[60%]" onClick={(e) => e.stopPropagation()}>
                <span
                    className="inline-block text-[10px] font-mono uppercase tracking-widest text-zinc-400 bg-black/60 border border-meye-border rounded px-2 py-1 truncate"
                    title={attachment.name}
                >
                    {attachment.name || (isPdf ? "document.pdf" : "attachment")}
                </span>
            </div>

            {/* Content — wrapper does NOT catch clicks; only the actual media does. */}
            <div className="pointer-events-none relative w-full h-full max-w-6xl max-h-[92vh] flex items-center justify-center">
                {isImage && (
                    <img
                        src={attachment.data}
                        alt={attachment.name || "attachment"}
                        onClick={(e) => e.stopPropagation()}
                        className="pointer-events-auto max-w-full max-h-full object-contain rounded-md shadow-2xl"
                        data-testid="lightbox-image"
                    />
                )}
                {isPdf && (
                    <iframe
                        title={attachment.name || "PDF preview"}
                        src={attachment.data}
                        onClick={(e) => e.stopPropagation()}
                        className="pointer-events-auto w-full h-full rounded-md bg-white shadow-2xl"
                        data-testid="lightbox-pdf"
                    />
                )}
                {!isImage && !isPdf && (
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="pointer-events-auto text-center text-zinc-300 font-mono text-sm p-8 border border-meye-border rounded-md bg-meye-card/90"
                    >
                        <FileText size={40} className="mx-auto mb-3 text-zinc-500" />
                        <p className="mb-1">{attachment.name}</p>
                        <p className="text-xs text-zinc-500 mb-4">Preview not supported — download to view.</p>
                        <a
                            href={attachment.data}
                            download={attachment.name || "attachment"}
                            className="inline-flex items-center gap-1.5 text-xs font-brand uppercase tracking-widest border border-meye-cyan/60 text-meye-cyan px-3 py-1.5 rounded-full hover:bg-meye-cyan/10"
                        >
                            <Download size={12} /> download
                        </a>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

export default MediaLightbox;
